/**
 * Encounter Charges Service
 * Manages encounter-specific charges including E&M codes, procedure charges, and manual charges
 */

import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || '';

// Default E&M code mappings by encounter type and complexity
const EM_CODE_MAPPINGS = {
  outpatient: {
    new: {
      minimal: { code: '99201', description: 'Office visit, new patient, minimal' },
      low: { code: '99202', description: 'Office visit, new patient, low complexity' },
      moderate: { code: '99203', description: 'Office visit, new patient, moderate complexity' },
      high: { code: '99204', description: 'Office visit, new patient, moderate to high complexity' },
      comprehensive: { code: '99205', description: 'Office visit, new patient, high complexity' }
    },
    established: {
      minimal: { code: '99211', description: 'Office visit, established patient, minimal' },
      low: { code: '99212', description: 'Office visit, established patient, straightforward' },
      moderate: { code: '99213', description: 'Office visit, established patient, low complexity' },
      high: { code: '99214', description: 'Office visit, established patient, moderate complexity' },
      comprehensive: { code: '99215', description: 'Office visit, established patient, high complexity' }
    }
  },
  emergency: {
    all: {
      minimal: { code: '99281', description: 'Emergency dept visit, straightforward' },
      low: { code: '99282', description: 'Emergency dept visit, low complexity' },
      moderate: { code: '99283', description: 'Emergency dept visit, moderate complexity' },
      high: { code: '99284', description: 'Emergency dept visit, high complexity' },
      comprehensive: { code: '99285', description: 'Emergency dept visit, very high complexity' }
    }
  },
  inpatient: {
    admission: {
      low: { code: '99221', description: 'Initial hospital care, low complexity' },
      moderate: { code: '99222', description: 'Initial hospital care, moderate complexity' },
      high: { code: '99223', description: 'Initial hospital care, high complexity' }
    },
    subsequent: {
      low: { code: '99231', description: 'Subsequent hospital care, straightforward' },
      moderate: { code: '99232', description: 'Subsequent hospital care, moderate complexity' },
      high: { code: '99233', description: 'Subsequent hospital care, high complexity' }
    },
    discharge: {
      short: { code: '99238', description: 'Hospital discharge day, 30 min or less' },
      long: { code: '99239', description: 'Hospital discharge day, more than 30 min' }
    }
  },
  telehealth: {
    new: {
      low: { code: '99202', description: 'Telehealth visit, new patient, low complexity', modifier: '95' },
      moderate: { code: '99203', description: 'Telehealth visit, new patient, moderate complexity', modifier: '95' },
      high: { code: '99204', description: 'Telehealth visit, new patient, high complexity', modifier: '95' }
    },
    established: {
      low: { code: '99212', description: 'Telehealth visit, established patient, straightforward', modifier: '95' },
      moderate: { code: '99213', description: 'Telehealth visit, established patient, low complexity', modifier: '95' },
      high: { code: '99214', description: 'Telehealth visit, established patient, moderate complexity', modifier: '95' }
    }
  },
  observation: {
    initial: {
      low: { code: '99218', description: 'Initial observation care, low complexity' },
      moderate: { code: '99219', description: 'Initial observation care, moderate complexity' },
      high: { code: '99220', description: 'Initial observation care, high complexity' }
    },
    discharge: {
      all: { code: '99217', description: 'Observation care discharge' }
    }
  },
  surgical: {
    preop: {
      all: { code: '99024', description: 'Postoperative follow-up visit, surgical package' }
    }
  }
};

/**
 * Get available E&M codes for an encounter type
 * @param {string} encounterType - Type of encounter
 * @returns {Array} Array of available E&M codes with descriptions
 */
export function getAvailableEMCodes(encounterType) {
  const normalizedType = (encounterType || 'outpatient').toLowerCase().replace('_visit', '').replace('_admission', '');
  const typeMapping = EM_CODE_MAPPINGS[normalizedType] || EM_CODE_MAPPINGS.outpatient;
  
  const codes = [];
  Object.entries(typeMapping).forEach(([category, levels]) => {
    Object.entries(levels).forEach(([level, codeInfo]) => {
      codes.push({
        code: codeInfo.code,
        description: codeInfo.description,
        modifier: codeInfo.modifier || null,
        category,
        level
      });
    });
  });
  
  return codes;
}

/**
 * Auto-detect E&M code based on encounter data
 * @param {Object} encounter - The encounter data
 * @param {boolean} isNewPatient - Whether this is a new patient
 * @returns {Object} Suggested E&M code
 */
export function detectEMCode(encounter, isNewPatient = false) {
  const encounterType = (encounter?.type || 'outpatient').toLowerCase().replace('_visit', '').replace('_admission', '');
  
  // Calculate complexity based on documentation
  const complexity = calculateComplexity(encounter);
  
  let category = isNewPatient ? 'new' : 'established';
  
  // Special handling for different encounter types
  if (encounterType === 'emergency') {
    category = 'all';
  } else if (encounterType === 'inpatient') {
    category = encounter?.isInitialAdmission ? 'admission' : 'subsequent';
  } else if (encounterType === 'observation') {
    category = 'initial';
  }
  
  const typeMapping = EM_CODE_MAPPINGS[encounterType] || EM_CODE_MAPPINGS.outpatient;
  const categoryMapping = typeMapping[category] || typeMapping.established || Object.values(typeMapping)[0];
  
  const codeInfo = categoryMapping[complexity] || categoryMapping.moderate || Object.values(categoryMapping)[0];
  
  return {
    code: codeInfo.code,
    description: codeInfo.description,
    modifier: codeInfo.modifier || null,
    complexity,
    autoDetected: true
  };
}

/**
 * Calculate encounter complexity based on documentation
 */
function calculateComplexity(encounter) {
  if (!encounter) return 'moderate';
  
  let score = 0;
  
  // Check diagnoses count
  const diagnosesCount = encounter.diagnoses?.length || 0;
  if (diagnosesCount >= 4) score += 3;
  else if (diagnosesCount >= 2) score += 2;
  else if (diagnosesCount >= 1) score += 1;
  
  // Check vital signs completeness
  const vitals = encounter.vitals || {};
  const vitalsCount = Object.values(vitals).filter(v => v && v.toString().trim()).length;
  if (vitalsCount >= 6) score += 2;
  else if (vitalsCount >= 4) score += 1;
  
  // Check notes length
  const notesLength = calculateNotesLength(encounter.notes);
  if (notesLength > 1500) score += 3;
  else if (notesLength > 800) score += 2;
  else if (notesLength > 300) score += 1;
  
  // Check procedures/orders count
  const proceduresCount = encounter.proceduresCount || 0;
  if (proceduresCount >= 5) score += 2;
  else if (proceduresCount >= 2) score += 1;
  
  // Map score to complexity level
  if (score >= 8) return 'comprehensive';
  if (score >= 6) return 'high';
  if (score >= 3) return 'moderate';
  if (score >= 1) return 'low';
  return 'minimal';
}

/**
 * Calculate total notes length
 */
function calculateNotesLength(notes) {
  if (!notes) return 0;
  
  let length = 0;
  if (typeof notes === 'string') {
    return notes.length;
  }
  
  Object.values(notes).forEach(note => {
    if (typeof note === 'string') {
      length += note.length;
    } else if (typeof note === 'object' && note !== null) {
      Object.values(note).forEach(subNote => {
        if (typeof subNote === 'string') {
          length += subNote.length;
        }
      });
    }
  });
  
  return length;
}

/**
 * Get encounter charges (saved charges for an encounter)
 * @param {string} encounterId - The encounter ID
 * @returns {Promise<Object>} Encounter charges data
 */
export async function getEncounterCharges(encounterId) {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${API_BASE}/billing/encounters/${encounterId}/charges`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    // If no charges saved yet, return empty structure
    if (error.response?.status === 404) {
      return {
        encounterId,
        emCode: null,
        emCodeOverride: null,
        procedureCharges: [],
        manualCharges: [],
        totalEstimate: 0
      };
    }
    console.error('Error getting encounter charges:', error);
    throw error;
  }
}

/**
 * Save encounter charges
 * @param {string} encounterId - The encounter ID
 * @param {Object} chargesData - The charges data to save
 * @returns {Promise<Object>} Saved charges data
 */
export async function saveEncounterCharges(encounterId, chargesData) {
  try {
    const token = await getAuthToken();
    const response = await axios.put(
      `${API_BASE}/billing/encounters/${encounterId}/charges`,
      chargesData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error saving encounter charges:', error);
    throw error;
  }
}

/**
 * Get estimated charges for an encounter (preview before billing)
 * @param {string} encounterId - The encounter ID
 * @param {string} facilityId - The facility ID for pricing
 * @returns {Promise<Object>} Estimated charges breakdown
 */
export async function getEncounterChargesEstimate(encounterId, facilityId) {
  try {
    const token = await getAuthToken();
    const response = await axios.get(
      `${API_BASE}/billing/encounters/${encounterId}/estimate`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting encounter charges estimate:', error);
    throw error;
  }
}

/**
 * Add a manual charge to an encounter
 * @param {string} encounterId - The encounter ID
 * @param {Object} charge - The charge to add
 * @returns {Promise<Object>} Updated charges
 */
export async function addManualCharge(encounterId, charge) {
  try {
    const token = await getAuthToken();
    const response = await axios.post(
      `${API_BASE}/billing/encounters/${encounterId}/charges/manual`,
      charge,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error adding manual charge:', error);
    throw error;
  }
}

/**
 * Remove a manual charge from an encounter
 * @param {string} encounterId - The encounter ID
 * @param {string} chargeId - The charge ID to remove
 * @returns {Promise<Object>} Updated charges
 */
export async function removeManualCharge(encounterId, chargeId) {
  try {
    const token = await getAuthToken();
    const response = await axios.delete(
      `${API_BASE}/billing/encounters/${encounterId}/charges/manual/${chargeId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error removing manual charge:', error);
    throw error;
  }
}

/**
 * Override E&M code for an encounter
 * @param {string} encounterId - The encounter ID
 * @param {Object} emCode - The E&M code override
 * @returns {Promise<Object>} Updated charges
 */
export async function overrideEMCode(encounterId, emCode) {
  try {
    const token = await getAuthToken();
    const response = await axios.put(
      `${API_BASE}/billing/encounters/${encounterId}/charges/em-override`,
      { emCode },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error overriding E&M code:', error);
    throw error;
  }
}

/**
 * Get procedure charges for an encounter from orders
 * @param {Array} procedures - Array of procedures/orders
 * @param {string} facilityId - Facility ID for pricing
 * @returns {Array} Array of charge items
 */
export function mapProceduresToCharges(procedures, facilityPricing = {}) {
  if (!procedures || !Array.isArray(procedures)) return [];
  
  return procedures
    .filter(proc => proc.status !== 'Cancelled')
    .map(proc => {
      // Try to get facility-specific pricing
      const facilityPrice = facilityPricing[proc.testCode] || facilityPricing[proc.cptCode];
      
      return {
        id: proc.id,
        type: 'procedure',
        cptCode: proc.cptCode || proc.testCode || '',
        description: proc.description || proc.testName || `${proc.testType}: ${proc.testCode}`,
        quantity: proc.quantity || 1,
        unitPrice: facilityPrice?.price || proc.price || 0,
        amount: (facilityPrice?.price || proc.price || 0) * (proc.quantity || 1),
        procedureId: proc.id,
        status: proc.status,
        orderDate: proc.orderDate,
        diagnosisPointers: proc.diagnosisPointers || [1]
      };
    });
}

export default {
  getAvailableEMCodes,
  detectEMCode,
  getEncounterCharges,
  saveEncounterCharges,
  getEncounterChargesEstimate,
  addManualCharge,
  removeManualCharge,
  overrideEMCode,
  mapProceduresToCharges
};
